import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { DatePicker } from "rsuite";
import {
  createAndUpdateStaff,
  uploadPlayerForm,
} from "../../../services/academicService";
import ImagePreviewModal from "../../components/ImagePreviewModal";
import { formatDate } from "../../../utils/helperFunction";

const AddStaff = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const editUser = location.state?.user || null;
  const mode = location.state?.mode || "add";
  const isEditMode = mode === "edit" && !!editUser;

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [previewModal, setPreviewModal] = useState(false);
  const [previewFileName, setPreviewFileName] = useState("");
  const [uploadedFormFiles, setUploadedFormFiles] = useState(null);
  const [uploadedImageFiles, setUploadedImageFiles] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    sex: "M",
    dob: null,
    education: "",
    address: "",
    designation: "",
    department: "",
    joiningDate: null,
  });

  useEffect(() => {
    if (isEditMode && editUser) {
      setFormData({
        name: editUser.name || "",
        email: editUser.email || "",
        phone: editUser.phoneNumber || "",
        sex: editUser.sex || "M",
        dob: editUser.dob ? new Date(editUser.dob) : null,
        education: editUser.education || "",
        address: editUser.address || "",
        designation: editUser.designation || "",
        department: editUser.department || "",
        joiningDate: editUser.joiningDate
          ? new Date(editUser.joiningDate)
          : null,
      });
      setUploadedFormFiles(editUser.staffImage || null);
    }
  }, [isEditMode, editUser]);

  const handleFormFileChange = async (e) => {
    try {
      const file = e.target.files[0]; //single file
      if (!file) return;

      //Upload to API
      const res = await uploadPlayerForm(file);

      //REPLACE instead of append
      setUploadedFormFiles(res);
    } catch (error) {
      console.error("Image upload failed:", error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleDateChange = (date, field) => {
    setFormData((prev) => ({ ...prev, [field]: date }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.email) newErrors.email = "Email is required";
    if (!formData.phone) newErrors.phone = "Mobile number is required";
    if (!formData.dob) newErrors.dob = "Date of Birth is required";
    if (!formData.education) newErrors.education = "Education is required";
    if (!formData.designation)
      newErrors.designation = "Designation is required";
    if (!formData.department) newErrors.department = "Department is required";
    if (!formData.joiningDate)
      newErrors.joiningDate = "Joining Date is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /* ================= SUBMIT ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);

    const payload = {
      name: formData.name,
      email: formData.email,
      phoneNumber: formData.phone,
      sex: formData.sex,
      dob: formData.dob ? formatDate(formData.dob) : null,
      education: formData.education,
      address: formData.address,
      designation: formData.designation,
      department: formData.department,
      joiningDate: formData.joiningDate ? formatDate(formData.joiningDate) : null,
      staffImage: uploadedFormFiles,
      ...(isEditMode && { id: editUser.id }),
    };

    try {
      await createAndUpdateStaff(payload);
      toast.success(
        `Staff ${isEditMode ? "updated" : "created"} successfully!`,
        { autoClose: 1000 },
      );
      navigate("/admin/all-staff");
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div>
        <div className="page-titles d-flex justify-content-between align-items-center">
          <h4>{isEditMode ? "Edit" : "Add"} Staff</h4>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/admin/all-staff">Staff</Link>
            </li>
            <li className="breadcrumb-item active">
              {isEditMode ? "Edit Staff" : "Add Staff"}
            </li>
          </ol>
        </div>

        <div className="card">
          <div className="card-header">
            <h5 className="card-title">{isEditMode ? "Edit" : "Add"} Staff</h5>
          </div>

          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div className="row">
                {/* NAME */}
                <div className="col-sm-6">
                  <label className="form-label">
                    Name <span className="text-danger">*</span>
                  </label>
                  <input
                    name="name"
                    placeholder="Enter full name"
                    className={`form-control ${errors.name && "is-invalid"}`}
                    value={formData.name}
                    onChange={handleChange}
                  />
                  <div className="invalid-feedback">{errors.name}</div>
                </div>

                {/* EMAIL */}
                <div className="col-sm-6">
                  <label className="form-label">
                    Email <span className="text-danger">*</span>
                  </label>
                  <input
                    name="email"
                    placeholder="Enter email address"
                    readOnly={isEditMode} // Make email readonly in edit mode
                    className={`form-control ${errors.email && "is-invalid"}`}
                    value={formData.email}
                    onChange={handleChange}
                  />
                  <div className="invalid-feedback">{errors.email}</div>
                </div>

                {/* PHONE */}
                <div className="col-sm-6">
                  <label className="form-label">
                    Mobile <span className="text-danger">*</span>
                  </label>
                  <input
                    name="phone"
                    placeholder="Enter mobile number"
                    className={`form-control ${errors.phone && "is-invalid"}`}
                    value={formData.phone}
                    onChange={handleChange}
                  />
                  <div className="invalid-feedback">{errors.phone}</div>
                </div>

                {/* DOB */}
                <div className="col-sm-6">
                  <div className="form-group">
                    <label className="form-label">
                      Date of Birth <span className="text-danger">*</span>
                    </label>
                    <div className="input-hasicon mb-xl-0 mb-3">
                      <DatePicker
                        value={formData.dob}
                        placeholder="Select Date of Birth"
                        className="picker-suit"
                        onChange={(date) => handleDateChange(date, "dob")}
                      />
                    </div>
                  </div>
                  <div className="text-danger">{errors.dob}</div>
                </div>

                {/* JOINING DATE */}
                <div className="col-sm-6">
                  <div className="form-group">
                    <label className="form-label">
                      Joining Date <span className="text-danger">*</span>
                    </label>
                    <div className="input-hasicon mb-xl-0 mb-3">
                      <DatePicker
                        placeholder="Select Joining Date"
                        className="picker-suit"
                        value={formData.joiningDate}
                        onChange={(date) =>
                          handleDateChange(date, "joiningDate")
                        }
                      />
                    </div>
                    <div className="text-danger">{errors.joiningDate}</div>
                  </div>
                </div>

                {/* EDUCATION */}
                <div className="col-sm-6">
                  <label className="form-label">
                    Education <span className="text-danger">*</span>
                  </label>
                  <input
                    name="education"
                    placeholder="Enter education"
                    className={`form-control ${errors.education && "is-invalid"}`}
                    value={formData.education}
                    onChange={handleChange}
                  />
                </div>

                {/* DESIGNATION */}
                <div className="col-sm-6">
                  <label className="form-label">
                    Designation <span className="text-danger">*</span>
                  </label>
                  <input
                    name="designation"
                    placeholder="Enter designation"
                    className={`form-control ${errors.designation && "is-invalid"}`}
                    value={formData.designation}
                    onChange={handleChange}
                  />
                </div>

                {/* DEPARTMENT */}
                <div className="col-sm-6">
                  <label className="form-label">
                    Department <span className="text-danger">*</span>
                  </label>
                  <input
                    name="department"
                    placeholder="Enter department"
                    className={`form-control ${errors.department && "is-invalid"}`}
                    value={formData.department}
                    onChange={handleChange}
                  />
                </div>

                {/* ADDRESS */}
                <div className="col-sm-6">
                  <label className="form-label">Address</label>
                  <input
                    name="address"
                    placeholder="Enter address"
                    className="form-control"
                    value={formData.address}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-sm-6">
                  <div className="form-group fallback w-100">
                    <label className="form-label">Staff Image</label>
                    <input
                      id="userImage"
                      type="file"
                      className="form-control"
                      accept="image/*"
                      onChange={handleFormFileChange}
                    />
                  </div>
                </div>
                <div className="col-sm-12 flex-row d-flex gap-4">
                  {uploadedImageFiles && (
                    <div className="col-sm-6">
                      <h6>Upload Image</h6>

                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "12px",
                        }}
                      >
                        <div className="d-flex align-items-center justify-content-between p-2 border rounded">
                          {/* File Name on the Left */}
                          <div
                            style={{
                              flex: 1,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {uploadedImageFiles?.fileName}
                          </div>

                          {/* Action Buttons on the Right */}
                          <div className="d-flex align-items-center gap-2">
                            <button
                              type="button"
                              className="btn btn-secondary btn-sm"
                              title="View"
                              onClick={() => {
                                setPreviewFileName(uploadedImageFiles.fileName);
                                setPreviewModal(true);
                              }}
                            >
                              <i className="fa fa-eye" />
                            </button>
                            <button
                              type="button"
                              className="btn btn-danger btn-sm"
                              title="Delete"
                              onClick={() => setUploadedImageFiles(null)}
                            >
                              <i className="fa fa-trash" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {uploadedFormFiles && (
                    <div className="col-sm-6">
                      <h6>Image File</h6>

                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "12px",
                        }}
                      >
                        <div className="d-flex align-items-center justify-content-between p-2 border rounded">
                          {/* File Name on the Left */}
                          <div
                            style={{
                              flex: 1,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {uploadedFormFiles?.fileName}
                          </div>

                          {/* Action Buttons on the Right */}
                          <div className="d-flex align-items-center gap-2">
                            <button
                              type="button"
                              className="btn btn-secondary btn-sm"
                              title="View"
                              onClick={() => {
                                setPreviewFileName(uploadedFormFiles.fileName);
                                setPreviewModal(true);
                              }}
                            >
                              <i className="fa fa-eye" />
                            </button>
                            <button
                              type="button"
                              className="btn btn-danger btn-sm"
                              title="Delete"
                              onClick={() => setUploadedFormFiles(null)}
                            >
                              <i className="fa fa-trash" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* ACTIONS */}
                <div className="col-12 mt-3">
                  <button
                    type="submit"
                    className="btn btn-primary me-2"
                    disabled={loading}
                  >
                    {loading ? "Submitting..." : "Submit"}
                  </button>
                  <Link to="/admin/all-staff" className="btn btn-danger light">
                    Cancel
                  </Link>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
      <ImagePreviewModal
        show={previewModal}
        onClose={() => setPreviewModal(false)}
        fileName={previewFileName}
      />
    </>
  );
};

export default AddStaff;
